/**
 * Call It In LLC — Create Task (Bulletproof UI Edition)
 * - Fixed: Maps Autocomplete targeting via inputProps ID
 * - Fixed: Direct Backend Sync (Bypasses sync.js webhook variables)
 * - Retains full Material Design "Crisp" look
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

// Template Components
import DashboardLayout from "../../../examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "../../../examples/Navbars/DashboardNavbar";
import MDBox from "../../../components/MDBox";
import MDTypography from "../../../components/MDTypography";
import MDButton from "../../../components/MDButton";
import MDSnackbar from "../../../components/MDSnackbar";
import MDInput from "../../../components/MDInput";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Typography from "@mui/material/Typography";

// Utils & Data
import PlacesAutocompleteInput from "../../../components/PlacesAutocompleteInput";
import { getDrivingMiles } from "../../../utils/distance";
import calcPrice from "../../../utils/calcPrice";
import { getRates } from "../../../utils/rates";
import { describeCreateLocationWarnings } from "../../../utils/dispatchOps";
import { seededStopCoords } from "../../../utils/savedLocationCoords";
import { getDrivers } from "../../../data/drivers";
import { v4 as uuidv4 } from "uuid";
import { useJsApiLoader } from "@react-google-maps/api";
import { getCustomers } from "../../../api/quickbooks";
import { createTask } from "../../../api/tasks";
import { request, listDrivers } from "../../../api/client";
import { searchAddressBook, saveToAddressBook } from "../../../utils/addressBook";
import { GOOGLE_MAPS_SCRIPT_ID, GOOGLE_MAPS_LIBRARIES } from "../../../config/googleMaps";
import LocationPicker from "../../../components/LocationPicker";
import { createLocation, getLocation, listLocations } from "../../../api/locations";

const TASKS_KEY = "ci_tasks";
const EDIT_KEY = "ci_task_edit";

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const money = (v) =>
  typeof v === "number" && Number.isFinite(v)
    ? v.toLocaleString(undefined, { style: "currency", currency: "USD" })
    : "—";

/**
 * Derive a stable organizationId slug from a human-readable client/company name.
 * Must produce identical output to slugFromName in PortalAccess.jsx so that
 * tasks created here and portal user accounts created there always share the
 * same organizationId value and the backend scoping query matches them.
 *
 * Algorithm: strip all non-alphanumeric characters (preserving spaces), then
 * replace runs of spaces with a single hyphen.  Does NOT replace punctuation
 * with a hyphen directly — that would turn "St. Mary's" into "st-mary-s"
 * rather than the correct "st-marys".
 *
 * Examples:
 *   "Storage Plus"     → "storage-plus"
 *   "Call It In LLC"   → "call-it-in-llc"
 *   "St. Mary's"       → "st-marys"
 *   "A&B Corp"         → "ab-corp"
 */
function slugFromName(name) {
  if (!name) return "";
  return String(name)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, "")  // remove punctuation, keep spaces
    .replace(/\s+/g, "-")          // spaces → single hyphen
    .replace(/^-+|-+$/g, "")      // trim edge hyphens
    .slice(0, 60);
}

// ── Address Book: location types for the Save dialog ─────────────────────────
const SAVE_LOC_TYPES = [
  { value: "vendor",         label: "Vendor" },
  { value: "hospital",       label: "Hospital" },
  { value: "surgery_center", label: "Surgery Center" },
  { value: "warehouse",      label: "Warehouse" },
  { value: "customer",       label: "Customer" },
  { value: "other",          label: "Other" },
];

/**
 * SaveLocationDialog
 *
 * Opens when dispatch clicks "Save to Address Book" next to a pickup or
 * drop-off address that was typed manually (i.e. no location is currently
 * selected).  Pre-fills from the task form fields and calls POST /locations/.
 * On success, calls onSaved(locationObject) so the parent can update the
 * linked location state without re-loading.
 */
function SaveLocationDialog({ open, onClose, leg, prefill, onSaved }) {
  const [sForm, setSForm] = useState({ name: "", location_type: "vendor", notes: "", _saving: false });
  const [sError, setSError] = useState("");

  useEffect(() => {
    if (open) {
      setSForm({
        name:          prefill?.facilityName || "",
        location_type: "vendor",
        notes:         prefill?.notes        || "",
        _saving: false,
      });
      setSError("");
    }
  }, [open, prefill]);

  const set = (k, v) => setSForm((p) => ({ ...p, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSError("");
    if (!sForm.name.trim()) return setSError("Location name is required.");

    setSForm((p) => ({ ...p, _saving: true }));
    try {
      const payload = {
        name:                  sForm.name.trim(),
        location_type:         sForm.location_type,
        // The full address string from PlacesAutocomplete goes into address_line_1.
        // Dispatch can refine city/state in the Address Book page later.
        address_line_1:        prefill?.address || "",
        delivery_instructions: sForm.notes.trim() || null,
      };
      const res  = await createLocation(payload);
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        // Build a local record so the parent can display the chip immediately
        onSaved({ ...payload, id: data.id, created_at: data.created_at, is_active: true });
        onClose();
      } else {
        setSError(data?.detail ?? `Save failed (HTTP ${res.status}).`);
      }
    } catch {
      setSError("Cannot reach the server. Check your connection.");
    } finally {
      setSForm((p) => ({ ...p, _saving: false }));
    }
  };

  const title = leg === "pickup"
    ? "Save Pickup Location to Address Book"
    : "Save Drop-Off Location to Address Book";

  return (
    <Dialog open={open} onClose={!sForm._saving ? onClose : undefined}
      maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "1rem" } }}>
      <DialogTitle sx={{ fontWeight: 700, pb: 0.5 }}>{title}</DialogTitle>
      <Divider />
      <DialogContent>
        <MDBox component="form" onSubmit={handleSubmit}
          sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>

          {sError && <Alert severity="error" onClose={() => setSError("")}>{sError}</Alert>}

          {/* Address preview — read-only confirmation */}
          {prefill?.address && (
            <MDBox sx={{
              background: "#f8fafc", border: "1px solid #e2e8f0",
              borderRadius: "8px", p: 1.5,
            }}>
              <Typography variant="caption" sx={{
                fontWeight: 700, color: "#94a3b8",
                textTransform: "uppercase", letterSpacing: "0.06em",
              }}>
                Address to Save
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5, color: "#1e293b" }}>
                {prefill.address}
              </Typography>
            </MDBox>
          )}

          <MDInput
            label="Location Name"
            value={sForm.name}
            onChange={(e) => set("name", e.target.value)}
            fullWidth required autoFocus
            disabled={sForm._saving}
            helperText="The name dispatch will see when searching the Address Book"
          />

          <FormControl fullWidth size="small" disabled={sForm._saving}>
            <InputLabel id="sl-type-lbl">Location Type</InputLabel>
            <Select
              labelId="sl-type-lbl"
              value={sForm.location_type}
              label="Location Type"
              onChange={(e) => set("location_type", e.target.value)}
              sx={{ height: 44 }}
            >
              {SAVE_LOC_TYPES.map(({ value, label }) => (
                <MenuItem key={value} value={value}>{label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <MDInput
            label="Delivery Instructions (Optional)"
            value={sForm.notes}
            onChange={(e) => set("notes", e.target.value)}
            fullWidth multiline rows={2}
            disabled={sForm._saving}
            placeholder="Access codes, dock location, receiving hours…"
          />

          <MDBox display="flex" gap={1.5} justifyContent="flex-end" mt={1}>
            <MDButton variant="outlined" color="secondary" onClick={onClose} disabled={sForm._saving}>
              Cancel
            </MDButton>
            <MDButton type="submit" variant="gradient" color="success"
              disabled={sForm._saving} sx={{ minWidth: 160 }}>
              {sForm._saving
                ? <CircularProgress size={18} color="inherit" />
                : "Save to Address Book"}
            </MDButton>
          </MDBox>
        </MDBox>
      </DialogContent>
    </Dialog>
  );
}

// ── ClientPicker helpers ──────────────────────────────────────────────────────
// Shared by the inline customer autocomplete below.  Customer is the
// billing/owning organization — it is independent of pickup and drop-off.

const CLIENT_TYPE_STYLE = {
  vendor:         { bg: "#eff6ff", text: "#1d4ed8" },
  customer:       { bg: "#f0fdf4", text: "#15803d" },
  hospital:       { bg: "#fdf4ff", text: "#7e22ce" },
  surgery_center: { bg: "#fff7ed", text: "#c2410c" },
  warehouse:      { bg: "#f0f9ff", text: "#0369a1" },
  other:          { bg: "#f8fafc", text: "#475569" },
};
const CLIENT_TYPE_LABEL = {
  vendor:         "Vendor",
  customer:       "Customer",
  hospital:       "Hospital",
  surgery_center: "Surgery Center",
  warehouse:      "Warehouse",
  other:          "Other",
};
const CLIENT_STATUS_DISPLAY = {
  archived:    { label: "Archived",    bg: "#fef3c7", text: "#b45309" },
  not_found:   { label: "Not Found",   bg: "#fee2e2", text: "#b91c1c" },
  unavailable: { label: "Unavailable", bg: "#f1f5f9", text: "#64748b" },
};

/**
 * Small chip shown beneath the Customer text field when a record from
 * the Address Book is linked.  Clearing removes only the link — the typed name
 * in the text field above is never touched.
 */
function ClientLinkedChip({ location, onClear }) {
  const locStatus  = location._status;
  const hasWarning = !!locStatus;
  const meta       = CLIENT_TYPE_STYLE[location.location_type] || CLIENT_TYPE_STYLE.other;
  const typeLabel  = CLIENT_TYPE_LABEL[location.location_type] || location.location_type || "";
  const cityState  = [location.city, location.state].filter(Boolean).join(", ");
  const statusD    = hasWarning ? CLIENT_STATUS_DISPLAY[locStatus] : null;

  return (
    <div style={{
      display: "flex", alignItems: "flex-start", gap: 8,
      padding: "5px 10px", borderRadius: 8, marginTop: 5,
      background: hasWarning ? "#fffbeb" : "#f0fdf4",
      border: `1px solid ${hasWarning ? "#fcd34d" : "#86efac"}`,
      fontSize: 11,
    }}>
      <span style={{
        fontFamily: "Material Icons", fontStyle: "normal",
        fontSize: 14, color: hasWarning ? "#b45309" : "#16a34a",
        flexShrink: 0, marginTop: 1,
      }}>
        {hasWarning ? "warning" : "link"}
      </span>

      <div style={{ flex: 1, minWidth: 0, lineHeight: 1.6 }}>
        <span style={{ color: "#475569" }}>Linked to Address Book · </span>
        {typeLabel && (
          <span style={{
            display: "inline-block", padding: "0px 5px", borderRadius: 5,
            fontSize: 10, fontWeight: 700,
            background: meta.bg, color: meta.text,
          }}>
            {typeLabel}
          </span>
        )}
        {cityState && (
          <span style={{ marginLeft: 5, color: "#94a3b8" }}>{cityState}</span>
        )}
        {statusD && (
          <span style={{
            marginLeft: 6, padding: "0px 5px", borderRadius: 5,
            fontSize: 10, fontWeight: 700,
            background: statusD.bg, color: statusD.text,
          }}>
            {statusD.label}
          </span>
        )}
        {hasWarning && (
          <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 1, fontStyle: "italic" }}>
            Name preserved · Clear to unlink or type a replacement
          </div>
        )}
      </div>

      <button
        type="button"
        onClick={onClear}
        style={{
          background: "none", border: "none", cursor: "pointer",
          padding: 0, color: "#94a3b8", flexShrink: 0,
          lineHeight: 1,
        }}
        title="Clear Address Book link — typed client name is preserved"
      >
        <span style={{ fontFamily: "Material Icons", fontStyle: "normal", fontSize: 15, display: "block" }}>
          close
        </span>
      </button>
    </div>
  );
}

export default function TaskCreate() {
  const rates = useMemo(() => getRates(), []);

  // Driver assignment datalist — sourced from the real backend roster
  // (GET /api/drivers, the same endpoint the Tasks page reassignment dropdown
  // uses) so every active role=driver employee (e.g. one just added via
  // Employees) is assignable here immediately. Falls back to the legacy
  // static/localStorage list (src/data/drivers.js) only if the roster
  // fetch fails, so the field never goes empty on a transient network error.
  const [driversList, setDriversList] = useState(() => getDrivers());
  useEffect(() => {
    let cancelled = false;
    listDrivers()
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((d) => {
        if (!cancelled && Array.isArray(d) && d.length > 0) setDriversList(d);
      })
      .catch(() => {
        // Backend roster unavailable — keep the static fallback already in state.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // ── Optional AxisSync network linkage ────────────────────────────────────────
  // Tasks created here are native Call It In courier jobs.  When spawned from
  // the "Add Task" button on the AxisSync Case List, the task is optionally
  // linked to a network case via linkedCaseId.  This linkage is additive — the
  // task is fully valid without it.
  const location = useLocation();
  const casePrefill = location.state || {};
  const [linkedCaseId, setLinkedCaseId] = useState(casePrefill.caseId || "");

  // ── Create vs. Edit disambiguation ──────────────────────────────────────
  // The Tasks page's "Edit" button loads an EXISTING task (including its
  // real id/createdAt/status) into the ci_task_edit draft and navigates
  // here so the same form can be reused to update it. But arriving at this
  // route ANY other way (sidebar "Create Task" link, browser back button,
  // etc.) must never silently resume that leftover draft — doing so caused
  // a brand-new submission to inherit the old task's id/createdAt/status,
  // so POST /tasks/ (INSERT OR REPLACE) silently overwrote the old task
  // instead of creating a new one, even though the toast reported success.
  const isEditSession = new URLSearchParams(location.search).get("edit") === "1";

  const { loadError } = useJsApiLoader({
    id: GOOGLE_MAPS_SCRIPT_ID,
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  // Notification States
  const [successSB, setSuccessSB] = useState(false);
  const [locationWarnSB, setLocationWarnSB] = useState(false);
  const [locationWarning, setLocationWarning] = useState("");
  const [errorSB, setErrorSB] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const openSuccessSB = () => setSuccessSB(true);
  const closeSuccessSB = () => setSuccessSB(false);
  const openErrorSB = (msg) => {
    setErrorMessage(msg);
    setErrorSB(true);
  };
  const closeErrorSB = () => setErrorSB(false);

  // QuickBooks Customers State
  const [qbCustomers, setQbCustomers] = useState([]);

  // Address Memory Engine — past addresses for the current client
  const [savedAddresses, setSavedAddresses] = useState([]);
  // Holds the address the dispatcher has highlighted in the dropdown
  // before they explicitly route it to Pickup or Delivery.
  const [selectedSavedAddress, setSelectedSavedAddress] = useState(null);

  // Address-book suggestions fed to PlacesAutocompleteInput dropdowns
  const [pickupSuggestions,  setPickupSuggestions]  = useState([]);
  const [dropoffSuggestions, setDropoffSuggestions] = useState([]);

  // ── Address Book location state ───────────────────────────────────────────
  // Separate from `form` so we don't persist large JSON blobs to localStorage.
  // All three are set to null on successful task creation.
  const [pickupLocation,  setPickupLocation]  = useState(null);
  const [dropoffLocation, setDropoffLocation] = useState(null);
  // Controls the "Save to Address Book" modal — leg: "pickup" | "dropoff"
  const [saveLocDialog, setSaveLocDialog] = useState({ open: false, leg: null });

  // ── Customer Address Book link (independent of pickup / drop-off) ──────────
  const [clientLocation,     setClientLocation]     = useState(null);
  // Dropdown state for the inline client autocomplete
  const [clientQuery,        setClientQuery]        = useState("");
  const [clientOptions,      setClientOptions]      = useState([]);
  const [clientDropdownOpen, setClientDropdownOpen] = useState(false);
  const [clientHighlight,    setClientHighlight]    = useState(-1);

  const [form, setForm] = useState(() => {
    if (isEditSession) {
      try {
        const cached = JSON.parse(localStorage.getItem(EDIT_KEY) || "null");
        if (cached && typeof cached === "object") {
          if (!cached.temperatureControl) cached.temperatureControl = "ambient";
          return cached;
        }
      } catch {}
    } else {
      // Not an explicit edit — discard any leftover draft (whether from a
      // previous "Edit" session or an abandoned new-task draft) so it can
      // never be silently resubmitted under a fresh "Save Task" click.
      localStorage.removeItem(EDIT_KEY);
    }
    return {
      clientName: "",
      companyName: "",
      driver: "",
      pickupFacilityName: "",
      pickupAddress: "",
      dropoffFacilityName: "",
      dropoffAddress: "",
      hospitalName: "",
      readyNow: false,
      deliverBy: "",
      base: rates.base ?? 55,
      useContract: false,
      contractPrice: "",
      liveMiles: "",
      deadheadMiles: "auto",
      waitMinutes: "",
      weight: "",
      holdoverNights: "",
      dedicatedHours: "",
      afterHours: false,
      pieceCount: "",
      temperatureControl: "ambient",
      notes: "",
      podRequirement: "none",
      pickupProofRequirement: "none",
    };
  });

  useEffect(() => {
    localStorage.setItem(EDIT_KEY, JSON.stringify(form));
  }, [form]);

  // Fetch QuickBooks Customers on Load
  useEffect(() => {
    async function fetchCustomers() {
      try {
        const response = await getCustomers();
        if (response.ok) {
          const data = await response.json();
          if (data.customers) setQbCustomers(data.customers);
        }
      } catch (err) {
        console.warn("QB fetch failed:", err);
      }
    }
    fetchCustomers();
  }, []);

  // loadError logging -- PlacesAutocompleteInput handles script loading and z-index internally
  useEffect(() => {
    if (loadError) {
      console.error(
        "Google Maps failed to load. Check API key, billing, and referrer restrictions."
      );
    }
  }, [loadError]);

  // Address Memory Engine — fetch saved addresses whenever client name changes
  useEffect(() => {
    const name = (form.clientName || form.companyName || "").trim();
    // Reset any previously staged address whenever the client changes
    setSelectedSavedAddress(null);
    if (name.length < 3) {
      setSavedAddresses([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const res = await request(`/clients/${encodeURIComponent(name)}/addresses`);
        if (res.ok) {
          const data = await res.json();
          setSavedAddresses(data.addresses || []);
        }
      } catch {
        setSavedAddresses([]);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [form.clientName, form.companyName]);

  // ── AxisSync pre-fill (optional) — auto-populate fields from Case List ──────
  useEffect(() => {
    if (!casePrefill.caseId) return;
    // Clear any stale draft so the case data wins
    localStorage.removeItem(EDIT_KEY);
    setLinkedCaseId(casePrefill.caseId);
    setForm((prev) => ({
      ...prev,
      clientName:    casePrefill.client   || prev.clientName,
      companyName:   casePrefill.client   || prev.companyName,
      pickupAddress: casePrefill.pickup   || prev.pickupAddress,
      dropoffAddress:casePrefill.dropoff  || prev.dropoffAddress,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Distance Auto-Calculator
  useEffect(() => {
    let cancelled = false;
    const a = (form.pickupAddress || "").trim();
    const b = (form.dropoffAddress || "").trim();
    const lm = String(form.liveMiles || "").trim().toLowerCase();
    const liveIsAuto = lm === "" || lm === "auto";

    if (!form.useContract && a && b && liveIsAuto) {
      const run = async () => {
        try {
          const miles = await getDrivingMiles(a, b);
          if (cancelled) return;
          if (typeof miles === "number" && Number.isFinite(miles)) {
            const adj = Math.max(miles, 0.01);
            setForm((f) => ({ ...f, liveMiles: Number(adj.toFixed(2)) }));
          }
        } catch {}
      };
      const t = setTimeout(run, 1000);
      return () => {
        cancelled = true;
        clearTimeout(t);
      };
    }
    return () => {
      cancelled = true;
    };
  }, [form.pickupAddress, form.dropoffAddress, form.useContract]);

  // ── Address-book suggestions (reactive, debounced) ────────────────────────
  useEffect(() => {
    setPickupSuggestions(searchAddressBook(form.pickupAddress));
  }, [form.pickupAddress]);

  useEffect(() => {
    setDropoffSuggestions(searchAddressBook(form.dropoffAddress));
  }, [form.dropoffAddress]);

  async function recalcLiveMiles() {
    const a = (form.pickupAddress || "").trim();
    const b = (form.dropoffAddress || "").trim();
    if (!a || !b) return;
    try {
      const miles = await getDrivingMiles(a, b);
      const adj = Math.max(Number(miles || 0), 0.01);
      setForm((f) => ({
        ...f,
        liveMiles: Number(adj.toFixed(2)),
      }));
    } catch {}
  }

  const preview = useMemo(() => {
    const contract = num(form.contractPrice);
    if (form.useContract && contract > 0) {
      return { base: 0, mileage: 0, total: contract, usingContract: true };
    }
    const ctx = {
      ...form,
      base: num(form.base),
      liveMiles:
        String(form.liveMiles).toLowerCase() === "auto"
          ? 0
          : num(form.liveMiles),
      deadheadMiles:
        String(form.deadheadMiles).toLowerCase() === "auto"
          ? 0
          : num(form.deadheadMiles),
    };
    const calc = calcPrice(ctx, rates);
    const fuel =
      Math.round((num(calc.base) + num(calc.mileage)) * 0.1 * 100) / 100;
    const total =
      num(calc.base) +
      num(calc.mileage) +
      num(calc.deadhead) +
      num(calc.weight) +
      num(calc.holdover) +
      num(calc.wait) +
      num(calc.afterHours) +
      num(calc.dedicated) +
      fuel;
    return { ...calc, fuel, total, usingContract: false };
  }, [form, rates]);

  const [saving, setSaving] = useState(false);

  // ── Address Book handlers ─────────────────────────────────────────────────

  /** Called when dispatch selects a saved pickup location from the picker. */
  const handlePickupLocationSelect = useCallback((loc) => {
    setPickupLocation(loc);
    const addrParts = [loc.address_line_1, loc.address_line_2].filter(Boolean);
    const cityStateZip = [loc.city, loc.state, loc.postal_code].filter(Boolean).join(", ");
    const fullAddr = [...addrParts, cityStateZip].filter(Boolean).join(", ");
    setForm((f) => ({
      ...f,
      pickupFacilityName: loc.name,
      ...(fullAddr ? { pickupAddress: fullAddr } : {}),
    }));
  }, []);

  /** Clears the saved pickup location.  Address fields are intentionally preserved. */
  const handlePickupLocationClear = useCallback(() => {
    setPickupLocation(null);
  }, []);

  /** Called when dispatch selects a saved drop-off location from the picker. */
  const handleDropoffLocationSelect = useCallback((loc) => {
    setDropoffLocation(loc);
    const addrParts = [loc.address_line_1, loc.address_line_2].filter(Boolean);
    const cityStateZip = [loc.city, loc.state, loc.postal_code].filter(Boolean).join(", ");
    const fullAddr = [...addrParts, cityStateZip].filter(Boolean).join(", ");
    setForm((f) => ({
      ...f,
      dropoffFacilityName: loc.name,
      hospitalName: loc.name,
      ...(fullAddr ? { dropoffAddress: fullAddr } : {}),
      // Pre-fill delivery notes from saved instructions only when dispatch
      // has not already entered anything in the notes field.
      ...(!f.notes.trim() && loc.delivery_instructions
        ? { notes: loc.delivery_instructions }
        : {}),
    }));
  }, []);

  /** Clears the saved drop-off location.  Address fields are intentionally preserved. */
  const handleDropoffLocationClear = useCallback(() => {
    setDropoffLocation(null);
  }, []);

  /**
   * Called by SaveLocationDialog when a new location is successfully created.
   * Associates the returned record with the appropriate leg of this task.
   */
  const handleLocationSaved = useCallback((newLoc) => {
    if (saveLocDialog.leg === "pickup") {
      setPickupLocation(newLoc);
    } else {
      setDropoffLocation(newLoc);
    }
  }, [saveLocDialog.leg]);

  // ── Client Address Book search (debounced) ────────────────────────────────
  // Fires whenever clientQuery changes.  Results feed the inline dropdown that
  // sits below the Customer text input.  The dropdown is hidden once a
  // record is linked (clientLocation != null) so the chip takes its place.
  useEffect(() => {
    if (!clientQuery || clientQuery.length < 2) {
      setClientOptions([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const res = await listLocations({ q: clientQuery, active: true, limit: 8 });
        if (res.ok) {
          const data = await res.json();
          setClientOptions(data.locations || []);
        }
      } catch {
        // Network error — silently skip; manual entry is always available
      }
    }, 250);
    return () => clearTimeout(t);
  }, [clientQuery]);

  // ── Customer handlers ─────────────────────────────────────────────────────
  const handleClientLocationSelect = useCallback((loc) => {
    setClientLocation(loc);
    setClientQuery("");
    setClientOptions([]);
    setClientDropdownOpen(false);
    setClientHighlight(-1);
    // Populate ONLY the visible name field — never touch pickup or drop-off
    setForm((prev) => ({ ...prev, clientName: loc.name, companyName: loc.name }));
  }, []);

  const handleClientLocationClear = useCallback(() => {
    // Remove only the link — the typed client name in the text field stays
    setClientLocation(null);
    setClientOptions([]);
    setClientQuery("");
  }, []);

  const handleClientKeyDown = useCallback((e) => {
    if (!clientDropdownOpen || clientOptions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setClientHighlight((h) => Math.min(h + 1, clientOptions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setClientHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter" && clientHighlight >= 0) {
      e.preventDefault();
      handleClientLocationSelect(clientOptions[clientHighlight]);
    } else if (e.key === "Escape") {
      setClientDropdownOpen(false);
      setClientHighlight(-1);
    }
  }, [clientDropdownOpen, clientOptions, clientHighlight, handleClientLocationSelect]);

  // ── Re-hydrate location pickers when editing an existing task ─────────────
  // Runs once on mount only.  The form is already loaded from localStorage at
  // this point; we mirror any stored location IDs / snapshots into the picker
  // state and then do a background live-record check to surface status warnings.
  useEffect(() => {
    /**
     * Restore one leg (pickup or dropoff) from the cached task data.
     *
     * Priority order:
     *   1. snapshot in raw_data  — shows chip immediately, no network wait
     *   2. live backend fetch    — background only, used to set _status flag
     *   3. no snapshot + live ID — fetch live record and use it directly
     *
     * None of these paths overwrite the task's stored address fields; those
     * come from the form's localStorage load and remain authoritative.
     */
    async function hydrateLeg(snapshot, locationId, setter) {
      if (!locationId) return; // older task — nothing to do

      if (snapshot) {
        // Immediate display using the snapshot (no network round-trip required)
        setter(snapshot);

        // Background: check whether the live record still exists and is active
        try {
          const res = await getLocation(locationId);
          if (res.status === 404) {
            setter((prev) => prev ? { ...prev, _status: "not_found" } : prev);
          } else if (res.ok) {
            const live = await res.json().catch(() => null);
            if (live && !live.is_active) {
              setter((prev) => prev ? { ...prev, _status: "archived" } : prev);
            }
            // If live.is_active the chip stays showing the green snapshot — correct
          }
          // Non-ok responses other than 404 are silently ignored; snapshot remains
        } catch {
          // Network / API error — flag as unavailable but keep snapshot displayed
          setter((prev) => prev ? { ...prev, _status: "unavailable" } : prev);
        }
      } else {
        // Has a location ID but no snapshot (task created before Phase 3).
        // Try to fetch the live record and use it as the chip data.
        try {
          const res = await getLocation(locationId);
          if (res.ok) {
            const live = await res.json().catch(() => null);
            if (live) {
              setter({
                ...live,
                _status: live.is_active ? undefined : "archived",
              });
            }
          } else if (res.status === 404) {
            // Record gone — we have no data at all; leave picker empty
            // (form fields still have the address strings from raw_data)
          }
        } catch {
          // Silently ignore — legacy task will still show address text fields
        }
      }
    }

    hydrateLeg(form.pickupSnapshot,  form.pickupLocationId,  setPickupLocation);
    hydrateLeg(form.dropoffSnapshot, form.dropoffLocationId, setDropoffLocation);
    // Customer link — same pattern; clientName text field is already
    // populated from localStorage so we only need to restore the chip/state.
    hydrateLeg(form.clientSnapshot,  form.clientLocationId,  setClientLocation);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Mount only — form is stable (read synchronously from localStorage)

  // DIRECT BACKEND SYNC (Bypasses external webhooks)
  async function handleSubmit(e) {
    e.preventDefault();
    if (saving) return;
    setSaving(true);

    const id = uuidv4();
    // Compute a charges object from the preview for invoicing.
    const charges = {
      base: preview.base,
      mileageCharge: preview.mileage,
      deadheadCharge: preview.deadhead,
      weightCharge: preview.weight,
      waitCharge: preview.wait,
      holdoverCharge: preview.holdover,
      dedicatedCharge: preview.dedicated,
      afterHoursCharge: preview.afterHours,
      fuelSurcharge: preview.fuel,
    };
    const normalizedClientName = (form.clientName || form.companyName || "").trim();
    const task = {
      id,
      createdAt: new Date().toISOString(),
      status: "Assigned",
      ...form,
      clientName: normalizedClientName,
      companyName: normalizedClientName || null,
      hospitalName: (form.dropoffFacilityName || form.hospitalName || "").trim() || null,
      // Derived silently from Customer — scopes this task to the vendor
      // org so portal users (vendor_rep role) can see it via GET /tasks/.
      // Example: "Call It In LLC" → "call-it-in"
      organizationId: slugFromName(normalizedClientName) || null,
      preview,
      total: preview.total,
      charges,
      // AxisSync link — present when spawned from the Case List "Add Task" button
      ...(linkedCaseId ? { linkedCaseId } : {}),
      // ── Address Book integration ──────────────────────────────────────────
      // IDs link the task to a saved location record.  Snapshots preserve the
      // full address/contact data so historical tasks remain accurate even if
      // the Address Book record is edited later.
      pickupLocationId:  pickupLocation?.id  || null,
      dropoffLocationId: dropoffLocation?.id || null,
      pickupSnapshot:    pickupLocation       || null,
      dropoffSnapshot:   dropoffLocation      || null,
      // Customer — independent of pickup and drop-off legs.
      // clientSnapshot carries only identity/contact fields; _status is a
      // runtime-only flag and is stripped before storage.
      clientLocationId: clientLocation?.id || null,
      clientSnapshot:   clientLocation
        ? (({ _status, ...rest }) => rest)(clientLocation)  // strip runtime flag
        : null,
      // Pre-seeded geocoords: the backend geocoder skips the external call when
      // these are already present (see _apply_geocode in main.py).
      // Only while the task's address text still matches the saved record: an
      // edited address must be geocoded fresh, not pinned to the old point.
      ...seededStopCoords(pickupLocation,  form.pickupAddress,  "pickup"),
      ...seededStopCoords(dropoffLocation, form.dropoffAddress, "dropoff"),
      // Contact fields surfaced to the driver (stored in raw_data, shown on task detail)
      pickupContactName:    pickupLocation?.contact_name  || null,
      deliveryContactName:  dropoffLocation?.contact_name || null,
      deliveryInstructions: form.notes || dropoffLocation?.delivery_instructions || null,
    };

    try {
      // 1. Local Save redundancy
      const current = JSON.parse(localStorage.getItem(TASKS_KEY) || "[]");
      current.unshift(task);
      localStorage.setItem(TASKS_KEY, JSON.stringify(current));

      // 2. Direct FastAPI Fetch
      const res = await createTask(task);
      const data = await res.json().catch(() => null);

      // The success notification must only fire once the backend has
      // actually confirmed creation — not merely because fetch() resolved.
      // Surface the backend's real error detail (FastAPI HTTPException
      // strings or pydantic validation arrays) instead of a generic message.
      if (!res.ok) {
        const detail = Array.isArray(data?.detail)
          ? data.detail.map((d) => d.msg || JSON.stringify(d)).join("; ")
          : data?.detail;
        throw new Error(detail || `Backend rejected the task (HTTP ${res.status}).`);
      }
      if (!data || data.status !== "success" || !data.id) {
        throw new Error("Backend did not confirm task creation (no task ID returned).");
      }

      // Persist addresses to local address book for future autocomplete
      if (form.pickupAddress)  saveToAddressBook({ address: form.pickupAddress,  clientName: form.clientName, facilityName: form.pickupFacilityName  });
      if (form.dropoffAddress) saveToAddressBook({ address: form.dropoffAddress, clientName: form.clientName, facilityName: form.dropoffFacilityName });

      // Tell dispatch right away if a stop could not be placed precisely
      // enough to geofence — otherwise the driver finds out at the door.
      const locationWarning = describeCreateLocationWarnings(data);
      if (locationWarning) {
        setLocationWarning(locationWarning);
        setLocationWarnSB(true);
      }

      // Success Reset
      openSuccessSB();
      localStorage.removeItem(EDIT_KEY);
      setLinkedCaseId("");
      setPickupLocation(null);
      setDropoffLocation(null);
      setClientLocation(null);
      setForm((f) => ({
        ...f,
        clientName: "",
        companyName: "",
        driver: "",
        pickupFacilityName: "",
        pickupAddress: "",
        dropoffFacilityName: "",
        dropoffAddress: "",
        hospitalName: "",
        notes: "",
        liveMiles: "",
        waitMinutes: "",
        weight: "",
        pieceCount: "",
        useContract: false,
        contractPrice: "",
      }));
    } catch (err) {
      console.error("Direct Sync Error:", err);
      openErrorSB(err?.message || "Failed to connect to the backend. Check your connection and try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3} px={3} sx={{ overflowX: "hidden", paddingBottom: "140px" }}>
        <form onSubmit={handleSubmit} className="max-w-5xl mx-auto">
          <MDBox mb={4}>
            <MDTypography variant="h3" fontWeight="bold">
              Create Task
            </MDTypography>
            <MDTypography variant="button" sx={{ color: "#334155" }}>
              Configure logistics, cargo, and billing details.
            </MDTypography>
          </MDBox>

            {/* AxisSync link banner — visible when spawned from Case List */}
          {linkedCaseId && (
            <MDBox
              mb={3}
              p={2}
              display="flex"
              alignItems="center"
              gap={1.5}
              sx={{
                background: "linear-gradient(135deg, #e0f2fe 0%, #f0fdf4 100%)",
                border: "1px solid #bae6fd",
                borderRadius: "12px",
              }}
            >
              <MDBox
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  background: "#0ea5e9",
                  flexShrink: 0,
                  boxShadow: "0 0 0 3px rgba(14,165,233,0.2)",
                }}
              />
              <MDTypography variant="button" fontWeight="bold" color="info">
                AxisSync Network Case:&nbsp;
              </MDTypography>
              <MDTypography variant="button" color="text" sx={{ fontFamily: "monospace", fontSize: 11 }}>
                {linkedCaseId}
              </MDTypography>
              <MDTypography variant="caption" sx={{ ml: "auto", color: "#475569" }}>
                This courier task is linked to a network case and will roll up to its invoice.
              </MDTypography>
            </MDBox>
          )}

          {/* Logistics & Routing Card */}
          <Card
            sx={{
              p: 4,
              mb: 4,
              borderRadius: "1rem",
              boxShadow: "0 4px 20px 0 rgba(0,0,0,0.05)",
            }}
          >
            <MDTypography
              variant="h6"
              fontWeight="bold"
              mb={3}
              display="flex"
              alignItems="center"
              gap={1}
            >
              <MDBox
                sx={{
                  width: 4,
                  height: 20,
                  bgcolor: "info.main",
                  borderRadius: 1,
                }}
              />
              Logistics & Routing
            </MDTypography>

            {loadError && (
              <MDBox mb={2}>
                <MDTypography variant="button" color="error" fontWeight="bold">
                  Address search unavailable — check your Google Maps API key.
                </MDTypography>
              </MDBox>
            )}

            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                {/* Customer — text field is always visible for manual entry.
                    An inline Address Book dropdown assists with linking a saved
                    record.  Customer, Pickup, and Drop-off remain independent. */}
                <MDBox sx={{ position: "relative" }}>
                  <MDInput
                    fullWidth
                    label="Customer"
                    value={form.clientName}
                    onChange={(e) => {
                      const value = e.target.value;
                      setForm((prev) => ({ ...prev, clientName: value, companyName: value }));
                      // Drive Address Book search from what the user types
                      setClientQuery(value);
                      if (!clientLocation) setClientDropdownOpen(true);
                    }}
                    onFocus={() => {
                      if (!clientLocation && form.clientName?.length >= 2)
                        setClientDropdownOpen(true);
                    }}
                    onBlur={() =>
                      setTimeout(() => {
                        setClientDropdownOpen(false);
                        setClientHighlight(-1);
                      }, 160)
                    }
                    onKeyDown={handleClientKeyDown}
                    inputProps={{ autoComplete: "off" }}
                  />

                  {/* Address Book dropdown — hidden once a record is linked */}
                  {clientDropdownOpen && !clientLocation && clientOptions.length > 0 && (
                    <div style={{
                      position: "absolute", top: "100%", left: 0, right: 0,
                      zIndex: 1400,
                      background: "#fff",
                      border: "1px solid #e2e8f0",
                      borderRadius: 10,
                      boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
                      maxHeight: 260,
                      overflowY: "auto",
                      marginTop: 4,
                    }}>
                      {clientOptions.map((loc, i) => {
                        const m = CLIENT_TYPE_STYLE[loc.location_type] || CLIENT_TYPE_STYLE.other;
                        const lbl = CLIENT_TYPE_LABEL[loc.location_type] || loc.location_type;
                        const cs  = [loc.city, loc.state].filter(Boolean).join(", ");
                        return (
                          <div
                            key={loc.id}
                            onMouseDown={() => handleClientLocationSelect(loc)}
                            style={{
                              padding: "9px 14px",
                              cursor: "pointer",
                              background: i === clientHighlight ? "#f1f5f9" : "transparent",
                              borderBottom: i < clientOptions.length - 1
                                ? "1px solid #f1f5f9" : "none",
                            }}
                          >
                            <div style={{ fontWeight: 700, fontSize: 13, color: "#1e293b" }}>
                              {loc.name}
                            </div>
                            <div style={{
                              display: "flex", alignItems: "center",
                              gap: 6, marginTop: 3, flexWrap: "wrap",
                            }}>
                              <span style={{
                                padding: "1px 6px", borderRadius: 6,
                                fontSize: 10, fontWeight: 700,
                                background: m.bg, color: m.text,
                              }}>
                                {lbl}
                              </span>
                              {cs && (
                                <span style={{ fontSize: 11, color: "#64748b" }}>{cs}</span>
                              )}
                              {loc.address_line_1 && (
                                <span style={{ fontSize: 11, color: "#94a3b8" }}>
                                  {loc.address_line_1}
                                </span>
                              )}
                              {loc.contact_name && (
                                <span style={{ fontSize: 11, color: "#94a3b8" }}>
                                  · {loc.contact_name}
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </MDBox>

                {/* Linked chip — appears below the text field when a record is selected */}
                {clientLocation && (
                  <ClientLinkedChip
                    location={clientLocation}
                    onClear={handleClientLocationClear}
                  />
                )}

                {/* QB customers datalist preserved for backward compatibility */}
                <datalist id="qb-customers">
                  {qbCustomers.map((c) => (
                    <option key={c.Id} value={c.DisplayName} />
                  ))}
                </datalist>
              </Grid>
              <Grid item xs={12} md={6}>
                <MDInput
                  fullWidth
                  label="Driver (Optional)"
                  value={form.driver}
                  onChange={(e) => setForm({ ...form, driver: e.target.value })}
                  inputProps={{ list: "drivers" }}
                />
                <datalist id="drivers">
                  {driversList.map((d) => (
                    <option key={d.id || d.name} value={d.name} />
                  ))}
                </datalist>
              </Grid>

              {/* ── Address Memory Engine ── */}
              {savedAddresses.length > 0 && (
                <Grid item xs={12}>
                  <MDBox
                    sx={{
                      background: "#f0f5ff",
                      border: "1px solid #c7d7fe",
                      borderRadius: "12px",
                      p: "12px 16px",
                    }}
                  >
                    <MDTypography
                      variant="caption"
                      fontWeight="bold"
                      sx={{ display: "block", mb: 1.5, color: "#3730a3", letterSpacing: "0.03em" }}
                    >
                      📍 Saved Locations for {form.clientName}
                    </MDTypography>

                    {/* Step 1 — pick an address from the dropdown (no auto-fill) */}
                    <MDBox sx={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                      <select
                        value={selectedSavedAddress || ""}
                        onChange={(e) => setSelectedSavedAddress(e.target.value || null)}
                        style={{
                          flex: "1 1 260px",
                          height: 40,
                          borderRadius: 8,
                          border: "1px solid #c7d7fe",
                          padding: "0 12px",
                          fontSize: 13,
                          color: selectedSavedAddress ? "#1e1b4b" : "#9ca3af",
                          background: "#fff",
                          cursor: "pointer",
                          outline: "none",
                        }}
                      >
                        <option value="">— Select a saved address —</option>
                        {savedAddresses.map((addr, i) => (
                          <option key={i} value={addr}>
                            {addr}
                          </option>
                        ))}
                      </select>

                      {/* Step 2 — explicit routing buttons, only shown once an address is staged */}
                      {selectedSavedAddress && (
                        <>
                          <button
                            type="button"
                            onClick={() => {
                              setForm((f) => ({ ...f, pickupAddress: selectedSavedAddress }));
                              setSelectedSavedAddress(null);
                            }}
                            style={{
                              border: "1px solid #3b82f6",
                              background: "#eff6ff",
                              color: "#1d4ed8",
                              borderRadius: 7,
                              padding: "7px 14px",
                              fontSize: 12,
                              fontWeight: 700,
                              cursor: "pointer",
                              whiteSpace: "nowrap",
                              lineHeight: 1.5,
                            }}
                          >
                            ↑ Set as Pickup
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setForm((f) => ({ ...f, dropoffAddress: selectedSavedAddress }));
                              setSelectedSavedAddress(null);
                            }}
                            style={{
                              border: "1px solid #10b981",
                              background: "#f0fdf4",
                              color: "#065f46",
                              borderRadius: 7,
                              padding: "7px 14px",
                              fontSize: 12,
                              fontWeight: 700,
                              cursor: "pointer",
                              whiteSpace: "nowrap",
                              lineHeight: 1.5,
                            }}
                          >
                            ↓ Set as Delivery
                          </button>
                        </>
                      )}
                    </MDBox>
                  </MDBox>
                </Grid>
              )}

              {/* ── Pickup section ── */}
              <Grid item xs={12} md={6}>
                {/* Address Book picker — sits above the manual fields */}
                <MDBox mb={1.5}>
                  <LocationPicker
                    label="Pickup — Address Book"
                    selected={pickupLocation}
                    onSelect={handlePickupLocationSelect}
                    onClear={handlePickupLocationClear}
                    placeholder="Search vendors, hospitals, warehouses…"
                  />
                </MDBox>

                {/* Manual fields — always visible and editable */}
                <MDInput
                  fullWidth
                  label="Pickup Facility Name"
                  placeholder="e.g. St. Mary's Medical Center"
                  value={form.pickupFacilityName}
                  onChange={(e) => setForm((f) => ({ ...f, pickupFacilityName: e.target.value }))}
                  sx={{ mb: 1.5 }}
                />
                <PlacesAutocompleteInput
                  label="Pickup Address"
                  placeholder="Start typing an address…"
                  value={form.pickupAddress}
                  onChange={(addr) => setForm((f) => ({ ...f, pickupAddress: addr }))}
                  recentSuggestions={pickupSuggestions}
                />

                {/* "Save to Address Book" — shown only when dispatch entered manually */}
                {!pickupLocation && form.pickupFacilityName.trim() && form.pickupAddress.trim() && (
                  <MDBox mt={1} display="flex" justifyContent="flex-end">
                    <button
                      type="button"
                      onClick={() => setSaveLocDialog({ open: true, leg: "pickup" })}
                      style={{
                        background: "none", border: "none", cursor: "pointer",
                        fontSize: 11, color: "#3b82f6", fontWeight: 700,
                        letterSpacing: "0.03em", textDecoration: "underline", padding: 0,
                      }}
                    >
                      + Save pickup to Address Book
                    </button>
                  </MDBox>
                )}
              </Grid>

              {/* ── Drop-off section ── */}
              <Grid item xs={12} md={6}>
                {/* Address Book picker */}
                <MDBox mb={1.5}>
                  <LocationPicker
                    label="Drop-Off — Address Book"
                    selected={dropoffLocation}
                    onSelect={handleDropoffLocationSelect}
                    onClear={handleDropoffLocationClear}
                    placeholder="Search hospitals, surgery centers, customers…"
                  />
                </MDBox>

                {/* Manual fields — always visible and editable */}
                <MDInput
                  fullWidth
                  label="Delivery Facility Name"
                  placeholder="e.g. General Hospital OR Suite 4B"
                  value={form.dropoffFacilityName}
                  onChange={(e) => setForm((f) => ({ ...f, dropoffFacilityName: e.target.value }))}
                  sx={{ mb: 1.5 }}
                />
                <PlacesAutocompleteInput
                  label="Delivery Address"
                  placeholder="Start typing an address…"
                  value={form.dropoffAddress}
                  onChange={(addr) => setForm((f) => ({ ...f, dropoffAddress: addr }))}
                  recentSuggestions={dropoffSuggestions}
                />

                {/* "Save to Address Book" — shown only when dispatch entered manually */}
                {!dropoffLocation && form.dropoffFacilityName.trim() && form.dropoffAddress.trim() && (
                  <MDBox mt={1} display="flex" justifyContent="flex-end">
                    <button
                      type="button"
                      onClick={() => setSaveLocDialog({ open: true, leg: "dropoff" })}
                      style={{
                        background: "none", border: "none", cursor: "pointer",
                        fontSize: 11, color: "#3b82f6", fontWeight: 700,
                        letterSpacing: "0.03em", textDecoration: "underline", padding: 0,
                      }}
                    >
                      + Save drop-off to Address Book
                    </button>
                  </MDBox>
                )}
              </Grid>
            </Grid>
          </Card>

          {/* Rates & Mileage Card */}
          <Card
            sx={{
              p: 4,
              mb: 4,
              borderRadius: "1rem",
              boxShadow: "0 4px 20px 0 rgba(0,0,0,0.05)",
            }}
          >
            <MDTypography
              variant="h6"
              fontWeight="bold"
              mb={3}
              display="flex"
              alignItems="center"
              gap={1}
            >
              <MDBox
                sx={{
                  width: 4,
                  height: 20,
                  bgcolor: "success.main",
                  borderRadius: 1,
                }}
              />
              Rates & Mileage
            </MDTypography>
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={4}>
                <MDInput
                  fullWidth
                  type="number"
                  label="Base Price ($)"
                  value={form.base}
                  onChange={(e) => setForm({ ...form, base: e.target.value })}
                />
                <MDBox mt={2} display="flex" alignItems="center">
                  <input
                    type="checkbox"
                    style={{ marginRight: "8px" }}
                    checked={form.useContract}
                    onChange={(e) => setForm({ ...form, useContract: e.target.checked })}
                  />
                  <MDTypography variant="button" fontWeight="bold" color="text">
                    Use Contract Price
                  </MDTypography>
                </MDBox>
              </Grid>
              <Grid item xs={12} md={8}>
                <MDBox display="flex" gap={2}>
                  <MDInput
                    fullWidth
                    label={`Live Miles ${
                      String(form.liveMiles).toLowerCase() === "auto" ? "(Auto)" : ""
                    }`}
                    value={String(form.liveMiles)}
                    onChange={(e) => setForm({ ...form, liveMiles: e.target.value })}
                  />
                  <MDButton onClick={recalcLiveMiles} variant="outlined" color="secondary">
                    Recalc
                  </MDButton>
                </MDBox>
              </Grid>
            </Grid>
            {form.useContract && (
              <MDBox mt={3} p={2} bgColor="grey-100" borderRadius="lg">
                <MDInput
                  fullWidth
                  type="number"
                  label="Contract Total Amount"
                  value={form.contractPrice}
                  onChange={(e) => setForm({ ...form, contractPrice: e.target.value })}
                />
              </MDBox>
            )}
          </Card>

          {/* Cargo & Handling Card */}
          <Card
            sx={{
              p: 4,
              mb: 10,
              borderRadius: "1rem",
              boxShadow: "0 4px 20px 0 rgba(0,0,0,0.05)",
            }}
          >
            <MDTypography
              variant="h6"
              fontWeight="bold"
              mb={3}
              display="flex"
              alignItems="center"
              gap={1}
            >
              <MDBox sx={{ width: 4, height: 20, bgcolor: "warning.main", borderRadius: 1 }} />
              Cargo & Handling
            </MDTypography>
            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <MDBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                  <MDTypography
                    component="label"
                    variant="button"
                    fontWeight="bold"
                    sx={{ color: "#334155" }}
                  >
                    Temperature Control *
                  </MDTypography>
                </MDBox>
                <select
                  style={{
                    width: "100%",
                    padding: "10px",
                    borderRadius: "8px",
                    border: "1px solid #d2d6da",
                    outline: "none",
                    color: "#495057",
                  }}
                  value={form.temperatureControl}
                  onChange={(e) => setForm({ ...form, temperatureControl: e.target.value })}
                >
                  <option value="ambient">Ambient (No Control)</option>
                  <option value="refrigerated">Refrigerated (2–8 °C)</option>
                  <option value="frozen">Frozen (≤ –20 °C)</option>
                </select>
              </Grid>
              <Grid item xs={12} md={3}>
                <MDInput
                  fullWidth
                  type="number"
                  label="Weight (lbs)"
                  value={form.weight}
                  onChange={(e) => setForm({ ...form, weight: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <MDInput
                  fullWidth
                  type="number"
                  label="Piece Count"
                  value={form.pieceCount}
                  onChange={(e) => setForm({ ...form, pieceCount: e.target.value })}
                />
              </Grid>
            </Grid>
            <MDBox
              mt={4}
              pt={4}
              borderTop="1px solid #eee"
              display="flex"
              alignItems="center"
              gap={2}
              flexWrap="wrap"
            >
              <MDBox display="flex" alignItems="center" sx={{ flexShrink: 0 }}>
                <input
                  type="checkbox"
                  style={{
                    marginRight: "8px",
                    width: "18px",
                    height: "18px",
                    flexShrink: 0,
                  }}
                  checked={!!form.afterHours}
                  onChange={(e) => setForm({ ...form, afterHours: e.target.checked })}
                />
                <MDTypography
                  variant="button"
                  fontWeight="bold"
                  sx={{ color: "#1E293B", whiteSpace: "nowrap" }}
                >
                  After-Hours
                </MDTypography>
              </MDBox>
              <MDInput
                fullWidth
                label="Delivery Notes (Access codes, instructions…)"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                sx={{ flex: "1 1 200px" }}
              />
            </MDBox>

            {/* Proof of Pickup requirement (two-leg flow) */}
            <MDBox mt={3} pt={3} borderTop="1px solid #eee">
              <MDBox mb={1} ml={0.5} lineHeight={0}>
                <MDTypography component="label" variant="button" fontWeight="bold" sx={{ color: "#1E293B" }}>
                  Proof of Pickup Required
                </MDTypography>
                <MDTypography variant="caption" display="block" mt={0.25} sx={{ color: "#475569" }}>
                  Driver must capture a pickup photo before confirming&nbsp;<strong>Picked Up</strong>.
                  Applies at the&nbsp;<em>Arrived (PU)</em>&nbsp;step. <em>No Proof</em>&nbsp;skips the photo gate.
                </MDTypography>
              </MDBox>
              <ToggleButtonGroup
                value={form.pickupProofRequirement}
                exclusive
                onChange={(_, v) => v && setForm((f) => ({ ...f, pickupProofRequirement: v }))}
                size="small"
                sx={{ flexWrap: "wrap", gap: 1, mt: 1 }}
              >
                {[
                  { value: "none",  label: "No Proof" },
                  { value: "photo", label: "📷 Photo" },
                ].map(({ value, label }) => (
                  <ToggleButton
                    key={value}
                    value={value}
                    sx={{
                      fontWeight: 700,
                      fontSize: 12,
                      borderRadius: "8px !important",
                      px: 2,
                      border: "1px solid #d2d6da !important",
                      "&.Mui-selected": {
                        background: value === "none" ? "#E0F2FE" : "#FEF3C7",
                        color:      value === "none" ? "#0369A1" : "#B45309",
                        borderColor: value === "none" ? "#7DD3FC !important" : "#FCD34D !important",
                      },
                    }}
                  >
                    {label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </MDBox>

            {/* Proof of Delivery requirement */}
            <MDBox mt={3} pt={3} borderTop="1px solid #eee">
              <MDBox mb={1} ml={0.5} lineHeight={0}>
                <MDTypography component="label" variant="button" fontWeight="bold" sx={{ color: "#1E293B" }}>
                  Proof of Delivery Required
                </MDTypography>
                <MDTypography variant="caption" display="block" mt={0.25} sx={{ color: "#475569" }}>
                  Driver cannot mark&nbsp;<strong>Delivered</strong> until the selected proof is
                  captured. <em>No Proof</em>&nbsp;unlocks delivery on confirmed GPS arrival.
                </MDTypography>
              </MDBox>
              <ToggleButtonGroup
                value={form.podRequirement}
                exclusive
                onChange={(_, v) => v && setForm((f) => ({ ...f, podRequirement: v }))}
                size="small"
                sx={{ flexWrap: "wrap", gap: 1, mt: 1 }}
              >
                {[
                  { value: "none",                label: "No Proof" },
                  { value: "photo",               label: "📷 Photo" },
                  { value: "signature",           label: "✍️ Signature" },
                  { value: "photo_and_signature", label: "📷 + ✍️ Both" },
                ].map(({ value, label }) => (
                  <ToggleButton
                    key={value}
                    value={value}
                    sx={{
                      fontWeight: 700,
                      fontSize: 12,
                      borderRadius: "8px !important",
                      px: 2,
                      border: "1px solid #d2d6da !important",
                      "&.Mui-selected": {
                        background: value === "none" ? "#E0F2FE" : "#DCFCE7",
                        color:      value === "none" ? "#0369A1" : "#15803D",
                        borderColor: value === "none" ? "#7DD3FC !important" : "#86EFAC !important",
                      },
                    }}
                  >
                    {label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </MDBox>
          </Card>

          {/* Fixed bottom bar with live estimate and save button */}
          <MDBox
            position="fixed"
            bottom="1.5rem"
            left="50%"
            sx={{
              transform: "translateX(-40%)",
              width: "calc(100% - 350px)",
              maxWidth: "850px",
            }}
            variant="gradient"
            bgColor="dark"
            borderRadius="xl"
            p={3}
            shadow="lg"
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            zIndex={100}
          >
            <MDBox>
              <MDTypography variant="caption" color="white" opacity={0.7} fontWeight="bold">
                LIVE ESTIMATE
              </MDTypography>
              <MDTypography variant="h4" color="white" fontWeight="bold">
                {money(preview.total)}
              </MDTypography>
            </MDBox>
            <MDButton variant="gradient" color="info" type="submit" disabled={saving}>
              {saving ? "Vaulting…" : "Save Task"}
            </MDButton>
          </MDBox>
        </form>

        {/* Save to Address Book dialog */}
        <SaveLocationDialog
          open={saveLocDialog.open}
          onClose={() => setSaveLocDialog((p) => ({ ...p, open: false }))}
          leg={saveLocDialog.leg}
          prefill={
            saveLocDialog.leg === "pickup"
              ? { facilityName: form.pickupFacilityName,  address: form.pickupAddress,  notes: "" }
              : { facilityName: form.dropoffFacilityName, address: form.dropoffAddress, notes: form.notes }
          }
          onSaved={handleLocationSaved}
        />

        {/* Notification snackbars */}
        <MDSnackbar
          color="success"
          icon="check"
          title="Task Vaulted"
          content="Successfully synced to the database."
          dateTime={new Date().toLocaleString()}
          open={successSB}
          onClose={closeSuccessSB}
          close={closeSuccessSB}
          bgWhite
        />
        <MDSnackbar
          color="warning"
          icon="location_off"
          title="Check the stop location"
          content={locationWarning}
          dateTime={new Date().toLocaleString()}
          open={locationWarnSB}
          onClose={() => setLocationWarnSB(false)}
          close={() => setLocationWarnSB(false)}
          bgWhite
        />
        <MDSnackbar
          color="error"
          icon="warning"
          title="Sync Failed"
          content={errorMessage}
          dateTime={new Date().toLocaleString()}
          open={errorSB}
          onClose={closeErrorSB}
          close={closeErrorSB}
          bgWhite
        />
      </MDBox>
    </DashboardLayout>
  );
}





















